import { useState } from 'react'
import {
  getDelegationTotals,
  getChildrenAtPath,
  sumPlannedQuantity
} from '../../utils/taskDelegationUtils'
import {
  OwnerId,
  Task,
  ErrorMessage,
  Mtds,
  SaveError,
  SaveChangesBanner,
  SinglePlan,
  useGetOwnerNameQueries
} from 'prove-common'
import { useGetOrganizationUnitsQuery } from 'prove-common'
import { DelegationAuditLog } from '../delegation-audit-log/DelegationAuditLog'
import { DelegationExplorer } from '../delegation-explorer/DelegationExplorer'
import { DelegationSummaryCard } from '../delegation-explorer/DelegationSummaryCard'
import { useDelegationRootPath } from '../delegation-explorer/useDelegationRootPath'
import { DelegationWorksheet } from '../delegation-worksheet/DelegationWorksheet'
import { delegationViewRoot } from '../../utils/taskDelegationAdapters'
import { useDelegationDraft } from './useDelegationDraft'
import { HIDDEN_DELEGATION_UNITS } from '../../config/hiddenDelegationUnits'
import {
  getEffectiveHiddenDelegationUnits,
  isDelegationPathHidden,
  visiblePlannedQuantity
} from '../../utils/delegationVisibility'

type TaskDelegationTabProps = {
  task: Task
  plan: SinglePlan
  refetchTask: () => Promise<unknown>
  onDirtyChange: (isDirty: boolean) => void
  hiddenUnits?: ReadonlySet<string>
}

export default function TaskDelegationTab({
  task,
  plan,
  refetchTask,
  onDirtyChange,
  hiddenUnits = HIDDEN_DELEGATION_UNITS
}: Readonly<TaskDelegationTabProps>) {
  const delegationRoot = useDelegationRootPath(plan)
  const draft = useDelegationDraft(task.id, refetchTask, onDirtyChange)
  const {
    query: delegationQuery,
    nodes,
    setNodes,
    isSaving,
    saveError,
    hasConflict
  } = draft
  const [isHistoryOpen, setHistoryOpen] = useState(false)
  const [view, setView] = useState<'organization' | 'worksheet'>('organization')
  const fullManagement =
    delegationQuery.data?.delegation.canManage === true ||
    task.canManage === true
  const allowedDivisions =
    delegationQuery.data?.delegation.redistributableDivisions ?? []
  const organization = useGetOrganizationUnitsQuery()
  const canReplaceDelegation = fullManagement || allowedDivisions.length > 0
  const canEditDelegation = (owner: OwnerId): boolean => {
    if (fullManagement) return true
    if (owner.type === 'region') return false
    const units = organization.data ?? []
    let current = units.find(
      (unit) => unit.id === owner.owner && unit.type === owner.type
    )
    const visited = new Set<string>()
    while (current?.parentId && !visited.has(current.id)) {
      visited.add(current.id)
      current = units.find((unit) => unit.id === current?.parentId)
      if (current?.type === 'region')
        return allowedDivisions.includes(`region:${current.id}`)
    }
    return false
  }
  const plannedQuantity = draft.plannedQuantity ?? task.quantity ?? 0
  const effectiveHiddenUnits = getEffectiveHiddenDelegationUnits(
    nodes,
    hiddenUnits
  )
  const rootPath = delegationViewRoot(nodes, delegationRoot.path)
  const rootLabel =
    rootPath.length === 1 &&
    delegationRoot.path[0]?.toString() === rootPath[0].toString()
      ? delegationRoot.label
      : undefined
  const rawSummaryChildren = getChildrenAtPath(nodes, rootPath)
  const { root: summaryRoot, allocated: summaryAllocated } =
    getDelegationTotals(nodes, rootPath, plannedQuantity)
  const summaryChildren = rawSummaryChildren
    .map((child) => ({
      child,
      value: visiblePlannedQuantity(
        child,
        [...rootPath, child.ownerId],
        effectiveHiddenUnits
      )
    }))
    .filter(
      ({ child }) =>
        !isDelegationPathHidden(
          [...rootPath, child.ownerId],
          effectiveHiddenUnits
        )
    )
  const summaryNames = useGetOwnerNameQueries(
    summaryChildren.map(({ child }) => child.ownerId.toString())
  )
  const summarySegments = summaryChildren.map(({ child, value }) => ({
    id: child.ownerId.toString(),
    name:
      summaryNames.data.find((owner) => owner.id === child.ownerId.toString())
        ?.name ?? child.ownerId.owner,
    value
  }))
  const summaryRootRetained = summaryRoot?.retainedQuantity ?? 0
  const hiddenAllocated =
    sumPlannedQuantity(rawSummaryChildren) -
    summaryChildren.reduce((sum, child) => sum + child.value, 0)
  if (hiddenAllocated > 0) {
    summarySegments.push({
      id: 'hidden-delegation-units',
      name: 'Skjulte enheter',
      value: hiddenAllocated
    })
  }
  if (summaryRootRetained > 0) {
    summarySegments.push({
      id: 'retained-at-root',
      name: rootLabel ?? summaryRoot?.ownerId.owner ?? 'Rotnivå',
      value: summaryRootRetained
    })
  }
  const viewTabs = (
    <Mtds.Togglegroup
      {...({ focusgroup: 'radiogroup' } as Record<string, string>)}
      aria-label='Visning av fordeling'
    >
      <Mtds.Togglegroup.Item
        name='delegation-beta-view'
        value='organization'
        checked={view === 'organization'}
        onChange={() => setView('organization')}
      >
        Hierarki
      </Mtds.Togglegroup.Item>
      <Mtds.Togglegroup.Item
        name='delegation-beta-view'
        value='worksheet'
        checked={view === 'worksheet'}
        onChange={() => setView('worksheet')}
      >
        Liste
      </Mtds.Togglegroup.Item>
    </Mtds.Togglegroup>
  )

  if (delegationRoot.isPending) return <Mtds.Skeleton />
  if (delegationRoot.isError) {
    return (
      <ErrorMessage error={new Error('Kunne ikke laste tilsynsdivisjonen')} />
    )
  }
  if (delegationQuery.isPending) return <Mtds.Skeleton />
  if (delegationQuery.isError && delegationQuery.data == null) {
    return <ErrorMessage error={delegationQuery.error} />
  }

  const { isValid, error } = draft.validation

  const editorProps = {
    plannedQuantity,
    delegations: nodes,
    onChange: setNodes,
    rootPath,
    rootLabel,
    navigation: viewTabs,
    canEditQuantity: canEditDelegation,
    hiddenUnits: effectiveHiddenUnits
  }

  return (
    <Mtds.Grid data-gap={5}>
      <Mtds.Flex
        data-justify='space-between'
        data-align='start'
        className='gap-6 max-lg:flex-col'
      >
        <Mtds.Grid data-gap={3} className='max-w-3xl'>
          <Mtds.Flex data-align='center' data-gap={2}>
            <Mtds.Heading as='h2' data-size='sm'>
              Fordeling
            </Mtds.Heading>
            <Mtds.HelpText aria-label='Mer informasjon om fordeling'>
              Fordel prøvene til divisjoner, avdelinger, seksjoner og team som
              skal ta dem ut.
            </Mtds.HelpText>
          </Mtds.Flex>
        </Mtds.Grid>
      </Mtds.Flex>
      <Mtds.Grid data-gap={5}>
        {task.canManage === false && !canReplaceDelegation && (
          <Mtds.Alert data-color='info'>
            Du har ikke ansvar som gir tilgang til å endre denne fordelingen.
          </Mtds.Alert>
        )}
        <DelegationSummaryCard
          rootLabel={rootLabel}
          samplesDelegated={summaryAllocated}
          samplesToDelegate={plannedQuantity}
          segments={summarySegments}
        />
        {view === 'organization' ? (
          <DelegationExplorer {...editorProps} />
        ) : (
          <DelegationWorksheet {...editorProps} />
        )}
      </Mtds.Grid>
      <Mtds.Details
        onToggle={(event) => setHistoryOpen(event.currentTarget.open)}
      >
        <Mtds.Details.Summary>Fordelingshistorikk</Mtds.Details.Summary>
        {isHistoryOpen && (
          <div className='pt-6'>
            <DelegationAuditLog task={task} />
          </div>
        )}
      </Mtds.Details>
      {draft.isDirty && (
        <SaveError
          error={saveError}
          action='lagre fordelingen'
          conflictMessage='En annen oppdatering pågår eller fordelingen kan ikke lagres slik den er. Utkastet er beholdt. Prøv å lagre igjen, eller kopier endringene dine og last siden på nytt for å hente siste fordeling.'
        >
          {hasConflict && (
            <p>
              Last inn siste versjon forkaster utkastet. Kopier endringene dine
              før du fortsetter.
            </p>
          )}
        </SaveError>
      )}
      {draft.isDirty && (
        <SaveChangesBanner
          isError={!isValid || saveError != null || hasConflict}
          disableSaveOnError={!isValid || hasConflict}
          isSaving={isSaving}
          errorText={
            hasConflict
              ? 'Fordelingen er endret av en annen bruker. Last inn siste versjon før du fortsetter.'
              : error
          }
          headerText={
            hasConflict ? 'Fordelingen er endret av en annen bruker' : undefined
          }
          discardButtonLabel={
            hasConflict ? 'Last inn siste versjon' : undefined
          }
          saveButtonLabel={saveError ? 'Prøv igjen' : 'Lagre'}
          onSave={() => void draft.save()}
          onDiscard={() => {
            if (hasConflict) void draft.reload()
            else draft.discard()
          }}
        />
      )}
    </Mtds.Grid>
  )
}
